//#region import libraries
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// custom components
import { CustomSpinner, Card } from "_atoms";
import { TableValue } from "_molecules";
import { ClickableTable, ManualPagination } from '_organisms';

// bootstrap
import Row from 'react-bootstrap/Row';

//tremor
import { Subtitle } from '@tremor/react';

// styling
import { Spacing, Colors } from '_styles';

//moment date
import moment from 'moment';

// redux
import { useSelector, useDispatch } from 'react-redux'
import { selectSelectedReward } from '_redux/slices/prm-club/rewards'
import { selectSelectedTransaction, setTransaction, getTransactions, selectTransactions, selectTransactionStatus, selectTransactionTotalCount } from '_redux/slices/prm-club/analytics'
import { getTransaction, selectPurchasesOption, selectRowsPerPage, setCurrentPage, setRowsPerPage, selectCurrentPage, selectTransaction, selectSearchValue } from '_redux/slices/marketplace/transaction'
import { useDebounce } from '_utils/hooks';
import { generatePaginationRang } from '_utils/functions';

//#endregion



const styles = {
    tableHeader: {
        width: '150px',
        //border: '1px solid black',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        fontSize: 14,
        opacity: 0.6,
        fontWeight: 'bold',
        color: Colors.TEXT_COLOR,
        marginLeft: Spacing.SCALE_10
    },
    tableCell: {
        //border: '1px solid red',
        padding: '10px',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '150px'
    }
};

const TransactionsList = (props) => {

    // nav
    const history = useHistory();

    //#region redux
    const dispatch = useDispatch();

    const selectedProduct = useSelector(selectSelectedReward);
    const selectedTransactions = useSelector(selectTransactions);
    const transactionInfo = useSelector(selectTransaction);
    const transactionsStatus = useSelector(selectTransactionStatus);
    const transactionTotalCount = useSelector(selectTransactionTotalCount);
    const purchaseOption = useSelector(selectPurchasesOption);
    const selectedTransaction = useSelector(selectSelectedTransaction);
    const rowsPerPage = useSelector(selectRowsPerPage);
    const currentPage = useSelector(selectCurrentPage);
    const searchValue = useSelector(selectSearchValue)
    const debouncedSearchValue = useDebounce(searchValue, 400); // Wait 400ms after user stops typing
    //#endregion    

    //#region local state
    const [currentRowsRange, setCurrentRowsRange] = useState("");
    //#endregion

    const Status = (props) => {

        const [color, setColor] = useState('grey')

        useEffect(() => {

            if (props.status) {
                if (props.status == 'pending' || props.status == 'payment-complete') {
                    setColor('grey')
                }
                if (props.status == 'approved') {
                    setColor('orange')
                }
                if (props.status == 'cancelled' || props.status === "payment-failed") {
                    setColor('red')
                }
                if (props.status == 'succeeded' || props.status == 'complete') {
                    setColor('green')
                }
            }
        }, [props]);

        return (
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', width: '80%' }}>

                {/* circle */}
                <div style={{
                    width: 8,
                    height: 8,
                    backgroundColor: color,
                    borderRadius: 50,
                    marginTop: Spacing.SCALE_4
                }} />

                {/* tagline */}
                <p style={{ fontSize: 14, opacity: 0.7, fontWeight: '300', marginLeft: Spacing.SCALE_4, color: color }}>
                    {
                        props.status == 'payment-complete' ? 'Payment Complete' :
                            props.status == 'pending' ? 'Pending' :
                                props.status == 'approved' ? 'Approved' :
                                    props.status == 'cancelled' ? 'Cancelled' :
                                        props.status == 'payment-failed' ? 'Payment Failed' :
                                            props.status == 'complete' ? 'Completed' : 'Succeeded'
                    }
                </p>
            </div>
        )
    }

    // table columns
    const columns = useMemo(() => [
        {
            Header: (<div style={styles.tableHeader}>Index</div>),
            id: 'rowIndex',
            Cell: ({ row }) => {
                const index = (currentPage - 1) * rowsPerPage + row.index + 1;
                return (
                    <div style={styles.tableCell}>
                        <TableValue title={index} />
                    </div>
                );
            }
        },
        {
            Header: (<div style={styles.tableHeader}>Name</div>),
            accessor: "name",
            Cell: ({ cell: { value } }) => (
                <div style={styles.tableCell}>
                    <TableValue title={value} />
                </div>
            )
        },
        {
            Header: (<div style={styles.tableHeader}>Type</div>),
            accessor: "type",
            Cell: ({ cell: { value } }) => (
                <div style={styles.tableCell}>
                    <TableValue title={value} />
                </div>
            )
        },
        {
            Header: (<div style={styles.tableHeader}>Purchase Date</div>),
            accessor: "dateSold",
            Cell: ({ cell: { value } }) => {
                const formattedDate = moment(value).format("DD/MM/YY hh:mm A");

                return (
                    <div style={styles.tableCell}>
                        <TableValue title={formattedDate} />
                    </div>
                );
            }
        },
        {
            Header: (<div style={styles.tableHeader}>Quantity</div>),
            accessor: "quantity",
            Cell: ({ cell: { value } }) => (
                <div style={styles.tableCell}>
                    <TableValue title={value} />
                </div>
            )
        },
        {
            Header: (<div style={styles.tableHeader}>Status</div>),
            accessor: "status",
            Cell: ({ cell: { value } }) => <Status status={value} />
        }
    ], [selectedProduct]);


    const handleClick = (i) => {
        let transaction = selectedTransactions[i];
        // get transaction information
        dispatch(getTransaction(transaction.transactionId));
        dispatch(setTransaction(transaction.transactionId));
        history.push('/transaction-information');
    }

    const handleRowsPerPageChange = (newRowsPerPage) => {
        dispatch(setRowsPerPage(newRowsPerPage));
        const newPageCount = Math.ceil(transactionTotalCount / newRowsPerPage);
        if (currentPage > newPageCount) {
            dispatch(setCurrentPage(newPageCount));
        }
    };

    //#region useEffect
    useEffect(() => {

        if (selectedProduct && selectedProduct.id && props?.dateRange && props?.dateRange[0] && props?.dateRange[1]) {
            const params = {
                productId: selectedProduct.id,
                startDate: moment(props?.dateRange[0]).format().split('T')[0],
                endDate: moment(props?.dateRange[1]).format().split('T')[0],
                status: purchaseOption,
                skip: (currentPage - 1) * rowsPerPage,
                limit: rowsPerPage,
                paxName: debouncedSearchValue
            }
            dispatch(getTransactions(params))
        }
    }, [selectedProduct, purchaseOption, props?.dateRange, rowsPerPage, currentPage, debouncedSearchValue]);

    useEffect(() => {
        if (transactionsStatus === "succeeded") {
            let paginationRowInfo = generatePaginationRang(
                transactionTotalCount,
                rowsPerPage,
                currentPage
            );
            setCurrentRowsRange(paginationRowInfo.currentRange);
        }

    }, [transactionsStatus, rowsPerPage, currentPage]);

    useEffect(() => {
        console.log('selectedTransactions', selectedTransactions)
    }, [selectedTransactions])

    //#endregion

    return (
        <Row>

            {transactionsStatus == 'loading' ?
                // loading spinner
                <CustomSpinner rowCenter={true} />
                :
                <Card>

                    {/* no transactions message */}
                    {!selectedTransactions.length && <Subtitle>There are no Transactions for {selectedProduct?.productTitle}</Subtitle>}

                    {/* list of transactions table */}
                    <ClickableTable
                        columns={columns}
                        data={selectedTransactions.length ? selectedTransactions : []}
                        handleClick={(i) => { handleClick(i) }}
                        selectedId={selectedTransaction}
                    />

                    {/* manual pagination */}
                    <ManualPagination
                        currentPage={currentPage}
                        setCurrentPage={(page) => dispatch(setCurrentPage(page))}
                        pageCount={Math.ceil(transactionTotalCount / rowsPerPage)}
                        currentRange={currentRowsRange}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={handleRowsPerPageChange}
                        totalCount={transactionTotalCount}
                        transaction={true}
                    />

                </Card>
            }
        </Row>
    );
};


export default TransactionsList;
